<script setup>
import { ref, onMounted, watch, computed, } from 'vue'
import { gApp } from '../State.js'
import {
  signInWithEmailAndPassword,
  signInWithPopup,
  GoogleAuthProvider,
  OAuthProvider,
  createUserWithEmailAndPassword,
  sendPasswordResetEmail,
} from 'firebase/auth'
import { prettyJson } from '../SharedUtils.js'
import { useRoute } from "vue-router"
import BasicModal from './utils/BasicModal.vue'

const route = useRoute();

const props = defineProps({
  signIn: Boolean
})

let username = ref("");
let password = ref("");
let retypePassword = ref("");

let errorMsg = ref("");
let statusMsg = ref("");
let isSubmitting = ref(false);

let passwordResetModal = ref(null);

function getAuthErrorMsg(error) {
  console.log(`Auth error: ${prettyJson(error)}`)
  // "null" means ignore the error (don't show a user message)
  let msgMap = {
    "auth/credential-already-in-use": `There is already an account with that email.`,
    "auth/email-already-in-use": `There is already an account with that email.`,
    "auth/invalid-email": `That email is invalid.`,
    "auth/invalid-credential": `Invalid username or password.`,
    "auth/wrong-password": `That password is incorrect.`,
    "auth/quota-exceeded": `Too many attempts. Access temporarily disabled. Please try again later.`,
    "auth/too-many-requests": `Too many attempts. Access temporarily disabled. Please try again later.`,
    "auth/weak-password": `Please choose a stronger password.`,
    "auth/account-exists-with-different-credential": "An account already exists with the same email but different sign-in credentials.",
    "auth/popup-blocked": "Sign-in popup was blocked by your browser.",
    "auth/popup-closed-by-user": null,
    "auth/cancelled-popup-request": null,
    "auth/user-cancelled": null,
  };
  if (error.code in msgMap) {
    return msgMap[error.code];
  } else {
    return `An error occurred (${error.code}). Please contact support@heatwise-hvac.com`
  }
}

// Note - currently unused
async function startSignInWithGoogle() {
  try {
    console.log("Trying to sign in with google");
    let userCredential = await signInWithPopup(gApp.auth, new GoogleAuthProvider());
    console.log("Signed in with google");
  } catch (err) {
    console.log("Error signing in with google:", err);
    let errMsg = getAuthErrorMsg(err);
    if (errMsg !== null) {
      errorMsg.value = errMsg;
    }
  }
}

async function startSignInWithMicrosoft() {
  try {
    console.log("Trying to sign in with Microsoft");
    const provider = new OAuthProvider('microsoft.com');
    // Optional: Add scopes if needed
    // provider.addScope('mail.read');
    // provider.addScope('calendars.read');
    
    let userCredential = await signInWithPopup(gApp.auth, provider);
    console.log("Signed in with Microsoft");
  } catch (err) {
    console.log("Error signing in with Microsoft:", err);
    let errMsg = getAuthErrorMsg(err);
    if (errMsg !== null) {
      errorMsg.value = errMsg;
    }
  }
}

async function completeSignIn() {
  if (isSubmitting.value) {
    return; // Prevent multiple submissions
  }
  
  isSubmitting.value = true;
  
  if (props.signIn) {
    try {
      console.log("Trying to sign in");
      if (!username.value) {
        errorMsg.value = "Please enter a valid email";
        return;
      }
      if (!password.value) {
        errorMsg.value = "Please enter a password";
        return;
      }
      errorMsg.value = "";
      statusMsg.value = "Signing in...";
      let userCredential = await signInWithEmailAndPassword(gApp.auth, username.value, password.value);
      console.log("Signed in");
      errorMsg.value = "";
    } catch (err) {
      console.log("Error signing in:", err);
      // errorMsg.value = `Could not sign in. Please check you entered the right username and password.`;
      let errMsg = getAuthErrorMsg(err);
      if (errMsg !== null) {
        errorMsg.value = errMsg;
        gApp.sendDeveloperFeedback('Error on Sign-In', `Error signing in for ${username.value}: ${err.message}`);
      }
    } finally {
      statusMsg.value = "";
      isSubmitting.value = false;
    }
  } else {
    try {
      if (!username.value) {
        errorMsg.value = "Please enter a valid email";
        return;
      }
      if (!password.value) {
        errorMsg.value = "Please enter a password";
        return;
      }
      if (password.value !== retypePassword.value) {
        errorMsg.value = "Passwords must match."
        return;
      }
      errorMsg.value = "";
      statusMsg.value = "Creating account...";
      await createUserWithEmailAndPassword(gApp.auth, username.value, password.value);
      console.log("Created account!");
      errorMsg.value = "";
    } catch (err) {
      console.log("Error signing up:", err);
      // errorMsg.value = `Please check you entered a valid username and password.`;
      let errMsg = getAuthErrorMsg(err);
      if (errMsg !== null) {
        errorMsg.value = errMsg;
        gApp.sendDeveloperFeedback('Error on Sign-up', `Error signing up for ${username.value}: ${err.message}`);
      }
    } finally {
      statusMsg.value = "";
      isSubmitting.value = false;
    }
  }
}

function reset() {
  errorMsg.value = null;
  username.value = "";
  password.value = "";
  retypePassword.value = "";
  isSubmitting.value = false;
}

function changeToSignUp() {
  gApp.router.push({name: 'signup'})
}

function changeToSignIn() {
  gApp.router.push({name: 'signin'})
}

function startPasswordReset() {
  passwordResetModal.value.showModal();
}

function submitPasswordReset() {
  if (!username.value) {
    gApp.toast(`Please enter a valid email.`, {type: 'error'});
    return;
  }
  sendPasswordResetEmail(gApp.auth, username.value)
  .then(() => {
    gApp.toast(`Password reset email sent to ${username.value}`);
  })
  .catch((err) => {
    gApp.toast(`Error sending password reset email: ${err.message}`, {type: 'error'});
  })
}

watch(gApp.userInfo, (newUserInfo) => {
  if (newUserInfo.loggedIn) {
    gApp.router.replace('/');
  }
}, {immediate: true});

watch(() => {
  return route.fullPath;
},
(newPath) => {
  reset();
}, {immediate: true})

</script>

<template>
  <div class="OuterContainer">
    <div class="ContentWrapper">
      <!-- Left side - Marketing content -->
      <div class="MarketingSection">
        <div class="MarketingContent">
          <div class="AppLogoDiv">
            <img class="AppLogo" src="../assets/cpu-fan-cooling-icon-white.png"/>
            <span class="AppTitle">HeatWise</span>
          </div>

          <h1 class="MarketingHeader">
            Load calculations,
            <span class="MarketingHeaderAccent">made simple</span>
          </h1>
          <p class="MarketingSubtext">
            Join the engineering firms and consultants who use HeatWise for fast,
            accurate heating and cooling loads — built on ASHRAE methods.
          </p>

          <div class="FeaturesList">
            <div class="FeatureItem">
              <span class="FeatureIconTile"><i class="bi-calculator"></i></span>
              <div>
                <h3 class="FeatureTitle">Accurate load calculations</h3>
                <p class="FeatureDescription">Modern ASHRAE methods with detailed space-by-space analysis.</p>
              </div>
            </div>

            <div class="FeatureItem">
              <span class="FeatureIconTile"><i class="bi-graph-up"></i></span>
              <div>
                <h3 class="FeatureTitle">Professional reports</h3>
                <p class="FeatureDescription">Detailed PDF and Excel reports for clients and permit applications.</p>
              </div>
            </div>

            <div class="FeatureItem">
              <span class="FeatureIconTile"><i class="bi-cloud-check"></i></span>
              <div>
                <h3 class="FeatureTitle">Cloud-based access</h3>
                <p class="FeatureDescription">Reach your projects from any device, with automatic sync.</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Right side - Sign in form -->
      <div class="SignInSection">
        <div class="SignIn">
          <div class="MobileLogo">
            <img class="MobileLogoImg" src="../assets/cpu-fan-cooling-icon.png"/>
            <span class="MobileLogoTitle">HeatWise</span>
          </div>
          <div class="FormHeader">
            <h1 class="MainHeader">{{ signIn ? 'Sign in to your account' : 'Create your account'}}</h1>
            <p class="MuteText">{{ signIn ? "Welcome back. Please sign in to continue." : 'Get started with HeatWise today.'}}</p>
          </div>
          <p v-if="errorMsg" class="FormMsg ErrorMsg"><i class="bi-exclamation-circle mr-xs"></i>{{ errorMsg }}</p>
          <p v-if="statusMsg" class="FormMsg StatusMsg"><i class="bi-hourglass-split mr-xs"></i>{{ statusMsg }}</p>
          <form @submit.prevent="completeSignIn" class="W100">
            <div class="Field">
              <label class="FieldLabel">Email</label>
              <input class="AuthInput" placeholder="you@company.com" v-model="username" @keyup.enter="completeSignIn" />
            </div>
            <div class="Field">
              <label class="FieldLabel">Password</label>
              <input class="AuthInput" type="password" placeholder="Enter your password" v-model="password" @keyup.enter="completeSignIn" />
              <div v-if="signIn" class="ForgotRow">
                <button class="TextBtn" type="button" @click="startPasswordReset">Forgot your password?</button>
              </div>
              <input v-if="!signIn" class="AuthInput mt-xs" type="password" placeholder="Confirm your password" v-model="retypePassword" @keyup.enter="completeSignIn" />
            </div>
          </form>
          <!-- Currently not used -->
          <!--<button @click="startSignInWithGoogle">Sign in with google</button>-->
          <button class="AuthBtn AuthBtnPrimary" @click="completeSignIn" type="submit" :disabled="isSubmitting">
            <i class="bi-box-arrow-in-right mr-xs" v-if="signIn && !isSubmitting"></i>
            <i class="bi-person-plus mr-xs" v-else-if="!signIn && !isSubmitting"></i>
            <i class="bi-hourglass-split mr-xs" v-if="isSubmitting"></i>
            {{ isSubmitting ? (signIn ? 'Signing in…' : 'Creating account…') : (signIn ? 'Sign in' : 'Sign up') }}
          </button>
          <div class="Divider">
            <span>or</span>
          </div>
          <div class="W100">
            <button class="AuthBtn AuthBtnSocial" @click="startSignInWithMicrosoft" type="button">
              <i class="bi-microsoft mr-xs"></i>
              Continue with Microsoft
            </button>
          </div>
          <p class="ToggleRow">
            {{signIn ? "Don't have an account?" : 'Already have an account?'}}
            <button class="ToggleBtn" @click="signIn ? changeToSignUp() :  changeToSignIn()">{{signIn ? "Sign up" : 'Sign in' }}</button>
          </p>
        </div>
      </div>
    </div>
  </div>
  <BasicModal ref="passwordResetModal" title="Reset Password" doneText="Send Email" @onDone="submitPasswordReset">
    <p class="mb-s">Enter your email address to reset your password.</p>
    <input class="AuthInput" placeholder="Email" v-model="username" @keyup.enter="submitPasswordReset" />
  </BasicModal>
</template>

<style scoped>
/*
 * Styling carried over from the marketing website (heatwise-hvac.com):
 * steel-blue primary, amber accent, Inter, hairline borders, soft shadows,
 * 6px control radius, faint engineering-drawing grid.
 */

/* ---- Design tokens (mirror of the website's Tailwind config) ---- */
.OuterContainer {
  --hw-primary-50: hsl(213, 46%, 97%);
  --hw-primary-100: hsl(213, 46%, 93%);
  --hw-primary-200: hsl(213, 43%, 84%);
  --hw-primary-500: hsl(215, 58%, 43%);
  --hw-primary-600: hsl(216, 66%, 36%);
  --hw-primary-700: hsl(217, 68%, 30%);
  --hw-primary-900: hsl(219, 54%, 17%);
  --hw-primary-950: hsl(221, 52%, 11%);
  --hw-accent-400: hsl(33, 88%, 57%);
  --hw-accent-500: hsl(30, 86%, 49%);
  --hw-ink-900: #0f172a;
  --hw-ink-700: #334155;
  --hw-ink-600: #475569;
  --hw-ink-500: #64748b;
  --hw-ink-400: #94a3b8;
  --hw-line: #e2e8f0;
  --hw-ground: #fbfcfd;
  --hw-shadow: 0 1px 2px hsl(216 40% 20% / 0.05), 0 4px 16px hsl(216 40% 20% / 0.07);
}

.OuterContainer {
  width: 100%;
  min-height: calc(100vh - var(--header-height, 56px));
  background-color: var(--hw-ground);
  display: flex;
  font-family: Inter, system-ui, -apple-system, "Segoe UI", Roboto, sans-serif;
  -webkit-font-smoothing: antialiased;
}

.ContentWrapper {
  display: flex;
  width: 100%;
  min-height: calc(100vh - var(--header-height, 56px));
}

/* ---------------- Left: marketing panel ---------------- */
.MarketingSection {
  position: relative;
  flex: 1;
  overflow: hidden;
  background-color: var(--hw-primary-950);
  color: #fff;
  padding: var(--xl);
  display: flex;
  align-items: center;
  justify-content: center;
}

.MarketingSection::before {
  content: '';
  position: absolute;
  inset: 0;
  background-image:
    linear-gradient(to right, hsl(0 0% 100% / 0.06) 1px, transparent 1px),
    linear-gradient(to bottom, hsl(0 0% 100% / 0.06) 1px, transparent 1px);
  background-size: 34px 34px;
  -webkit-mask-image: radial-gradient(ellipse 90% 70% at 30% 0%, #000 30%, transparent 100%);
  mask-image: radial-gradient(ellipse 90% 70% at 30% 0%, #000 30%, transparent 100%);
  pointer-events: none;
}

.MarketingSection::after {
  content: '';
  position: absolute;
  top: -140px;
  left: 10%;
  width: 480px;
  height: 320px;
  background: hsl(215 58% 43% / 0.25);
  filter: blur(90px);
  pointer-events: none;
}

.MarketingContent {
  position: relative;
  max-width: 460px;
  width: 100%;
}

.AppLogoDiv {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: var(--xl);
}

.AppLogo {
  width: 30px;
}

.AppTitle {
  font-size: 20px;
  font-weight: 600;
  letter-spacing: -0.02em;
  color: #fff;
}

.MarketingHeader {
  margin: 0;
  font-size: 42px;
  font-weight: 600;
  line-height: 1.1;
  letter-spacing: -0.025em;
  color: #fff;
}

.MarketingHeaderAccent {
  color: var(--hw-primary-200);
}

.MarketingSubtext {
  margin: 18px 0 0;
  font-size: 17px;
  line-height: 1.6;
  color: hsl(213 40% 88% / 0.75);
}

.FeaturesList {
  display: flex;
  flex-direction: column;
  gap: 20px;
  margin-top: 36px;
  padding-top: 28px;
  border-top: 1px solid hsl(0 0% 100% / 0.12);
}

.FeatureItem {
  display: flex;
  gap: 14px;
  align-items: flex-start;
}

.FeatureIconTile {
  flex-shrink: 0;
  width: 36px;
  height: 36px;
  border-radius: 8px;
  display: grid;
  place-items: center;
  background: hsl(0 0% 100% / 0.07);
  border: 1px solid hsl(0 0% 100% / 0.12);
  color: #fff;
  font-size: 16px;
}

.FeatureTitle {
  font-size: 15px;
  font-weight: 600;
  margin: 0 0 3px;
  color: #fff;
}

.FeatureDescription {
  font-size: 14px;
  line-height: 1.5;
  color: hsl(213 40% 88% / 0.65);
  margin: 0;
}

/* ---------------- Right: form panel ---------------- */
.SignInSection {
  flex: 0 0 500px;
  background-color: #fff;
  border-left: 1px solid var(--hw-line);
  padding: var(--xl);
  display: flex;
  align-items: center;
  justify-content: center;
}

.SignIn {
  width: 100%;
  max-width: 380px;
  display: flex;
  flex-direction: column;
  align-items: stretch;
}

.MobileLogo {
  display: none;
  align-items: center;
  gap: 10px;
  margin-bottom: 28px;
}

.MobileLogoImg { width: 28px; }

.MobileLogoTitle {
  font-size: 19px;
  font-weight: 600;
  letter-spacing: -0.02em;
  color: var(--hw-ink-900);
}

.FormHeader {
  margin-bottom: 28px;
}

.MainHeader {
  margin: 0;
  line-height: 1.2;
  font-weight: 600;
  font-size: 26px;
  letter-spacing: -0.02em;
  color: var(--hw-ink-900);
}

.MuteText {
  margin: 8px 0 0;
  color: var(--hw-ink-500);
  font-size: 15px;
  line-height: 1.5;
}

.W100 { width: 100%; }

.Field {
  margin-bottom: 16px;
}

.FieldLabel {
  display: block;
  font-weight: 600;
  font-size: 13px;
  margin-bottom: 6px;
  color: var(--hw-ink-700);
}

/*
 * The global stylesheet defines
 *   input:not([type="radio"]):not([type="checkbox"]) { padding: 4px; height: 32px; ... }
 * which out-specifies a plain class. Match that selector shape here so these
 * overrides win WITHOUT !important and WITHOUT touching inputs anywhere else
 * in the app (scoped styles only apply within this component).
 */
input.AuthInput:not([type="radio"]):not([type="checkbox"]) {
  box-sizing: border-box;
  width: 100%;
  height: auto;
  padding: 11px 14px;
  border: 1px solid #cbd5e1;
  border-radius: 6px;
  font-size: 14px;
  line-height: 1.4;
  font-family: inherit;
  color: var(--hw-ink-900);
  background-color: #fff;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}

.AuthInput::placeholder { color: var(--hw-ink-400); }

input.AuthInput:not([type="radio"]):not([type="checkbox"]):focus {
  border-color: var(--hw-primary-500);
  box-shadow: 0 0 0 3px hsl(215 58% 43% / 0.15);
  outline: none;
}

.mt-xs { margin-top: 8px; }

.ForgotRow {
  margin-top: 8px;
  text-align: right;
}

.TextBtn {
  color: var(--hw-ink-500);
  transition: color 0.15s ease;
  padding: 0;
  background: none;
  border: none;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
}

.TextBtn:hover {
  color: var(--hw-primary-600);
}

/* Buttons */
.AuthBtn {
  width: 100%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 11px 16px;
  border-radius: 6px;
  font-weight: 600;
  font-size: 14px;
  font-family: inherit;
  letter-spacing: -0.01em;
  text-transform: none;
  cursor: pointer;
  transition: background-color 0.15s ease, border-color 0.15s ease, transform 0.15s ease, box-shadow 0.15s ease;
}

.AuthBtn:hover { transform: translateY(-1px); }

.AuthBtnPrimary {
  margin-top: 20px;
  background-color: var(--hw-primary-600);
  color: #fff;
  border: 1px solid var(--hw-primary-700);
  box-shadow: var(--hw-shadow);
}

.AuthBtnPrimary:hover {
  background-color: var(--hw-primary-700);
}

.AuthBtnPrimary:disabled {
  background-color: var(--hw-ink-400);
  border-color: var(--hw-ink-400);
  box-shadow: none;
  transform: none;
  cursor: not-allowed;
}

.AuthBtnSocial {
  background-color: #fff;
  color: var(--hw-ink-700);
  border: 1px solid #cbd5e1;
}

.AuthBtnSocial:hover {
  border-color: var(--hw-primary-200);
  background-color: var(--hw-primary-50);
}

/* Divider */
.Divider {
  display: flex;
  align-items: center;
  text-align: center;
  margin: 18px 0;
  width: 100%;
}

.Divider::before,
.Divider::after {
  content: '';
  flex: 1;
  height: 1px;
  background: var(--hw-line);
}

.Divider span {
  padding: 0 12px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  color: var(--hw-ink-400);
}

/* Messages */
.FormMsg {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  font-size: 14px;
  margin: 0 0 16px;
  text-align: center;
}

.ErrorMsg { color: #dc2626; }

.StatusMsg {
  color: var(--hw-primary-600);
  font-weight: 500;
}

/* Toggle */
.ToggleRow {
  display: flex;
  gap: 6px;
  justify-content: center;
  margin-top: 24px;
  font-size: 14px;
  color: var(--hw-ink-500);
}

.ToggleBtn {
  font-size: 14px;
  color: var(--hw-primary-600);
  font-weight: 600;
  transition: color 0.15s ease;
  padding: 0;
  background: none;
  border: none;
  cursor: pointer;
}

.ToggleBtn:hover {
  color: var(--hw-primary-700);
}

/* ---------------- Responsive ---------------- */
@media (max-width: 1024px) {
  .ContentWrapper { flex-direction: column; }

  .MarketingSection {
    flex: none;
    min-height: 42vh;
    padding: var(--l);
  }

  .MarketingHeader { font-size: 32px; }
  .FeaturesList { display: none; }

  .SignInSection {
    flex: 1;
    border-left: none;
    border-top: 1px solid var(--hw-line);
  }
}

@media (max-width: 640px) {
  .MarketingSection { display: none; }

  .SignInSection {
    flex: 1;
    border-top: none;
    padding: var(--l) var(--m);
    align-items: flex-start;
    padding-top: 12vh;
  }

  .MobileLogo { display: flex; }
}
</style>
